<template>
  <div class="dashboard-bg">
    <div class="dashboard-container container-responsive">
      <!-- Welcome Message -->
      <div class="welcome-section">
        <h1 class="welcome-title">
          Welcome back, {{ userDisplayName }}!
        </h1>
        <p class="welcome-subtitle">
          {{ getWelcomeMessage() }}
        </p>
             </div>
       
       <!-- QA Agent Specific Section -->
       <div v-if="isQAAgent" class="qa-agent-section">
         <div class="content-card">
           <div class="card-header">
             <h3 class="card-title">Your Assigned Questions</h3>
             <v-btn variant="text" size="small" color="#002060" @click="navigateToQA">
               View All Questions
             </v-btn>
           </div>
           <div class="qa-agent-content">
             <div class="qa-agent-info">
                <v-icon size="48" color="#002060" class="qa-agent-icon">mdi-help-circle</v-icon>
               <div class="qa-agent-text">
                 <h4>Welcome to your QA workspace</h4>
                 <p>You have <strong>{{ qaWorkload.pending }}</strong> pending questions and <strong>{{ qaWorkload.total }}</strong> total questions assigned to you.</p>
               </div>
             </div>
             <div class="qa-agent-actions">
               <v-btn 
                 color="#002060" 
                 variant="elevated" 
                 size="large"
                 @click="navigateToQA"
                 class="qa-action-btn"
               >
                 <v-icon left>mdi-help-circle</v-icon>
                 View My Assigned Questions
               </v-btn>
               <v-btn 
                 color="success" 
                 variant="outlined" 
                 size="large"
                 @click="requestMoreQuestions"
                 class="qa-action-btn ml-3"
                 :loading="requestingQuestions"
               >
                 <v-icon left>mdi-plus</v-icon>
                 Request More Questions
               </v-btn>
             </div>
           </div>
         </div>
       </div>
       
       <!-- Statistics Cards Row - Hidden for QA Agents -->
       <div v-if="showStatsForQAAgent" class="stats-row">
         <!-- User Management Stats - Only for Executive and Admin -->
         <div v-if="showUserManagement" class="stat-card">
           <div class="stat-icon users-icon">
             <v-icon size="32" color="white">mdi-account-group</v-icon>
           </div>
           <div class="stat-content">
             <h3 class="stat-number">{{ stats.totalUsers }}</h3>
             <p class="stat-label">Total Users</p>
             <span class="stat-change positive">+12% from last month</span>
           </div>
         </div>

         <!-- Client Stats - Available for Executive, Admin, and Employee -->
         <div v-if="showClientManagement" class="stat-card">
           <div class="stat-icon clients-icon">
             <v-icon size="32" color="white">mdi-account-tie</v-icon>
           </div>
           <div class="stat-content">
             <h3 class="stat-number">{{ stats.totalClients }}</h3>
             <p class="stat-label">Active Clients</p>
             <span class="stat-change positive">+8% from last month</span>
           </div>
         </div>

         <!-- QA Stats - Available for all roles except QA Agent -->
         <div class="stat-card">
           <div class="stat-icon questions-icon">
             <v-icon size="32" color="white">mdi-help-circle</v-icon>
           </div>
           <div class="stat-content">
             <h3 class="stat-number">{{ stats.pendingQuestions }}</h3>
             <p class="stat-label">Pending Q&A</p>
             <span class="stat-change neutral">{{ stats.answeredToday }} answered today</span>
           </div>
         </div>

         <!-- Members Stats - Only for Executive and Admin -->
         <div v-if="showUserManagement" class="stat-card">
           <div class="stat-icon members-icon">
             <v-icon size="32" color="white">mdi-account-multiple</v-icon>
           </div>
           <div class="stat-content">
             <h3 class="stat-number">{{ stats.totalMembers }}</h3>
             <p class="stat-label">Total Members</p>
             <span class="stat-change positive">{{ stats.teamMembers }} team members</span>
           </div>
         </div>
       </div>

             <!-- Main Content Row - Hidden for QA Agents -->
       <div v-if="showQuickActionsForQAAgent" class="content-row">
         <!-- Recent Activity Card - Only for Executive and Admin -->
         <div v-if="showActivityLog" class="content-card activity-card">
           <div class="card-header">
             <h3 class="card-title">Recent Activity</h3>
              <v-btn variant="text" size="small" color="#002060">View All</v-btn>
           </div>
           <div class="activity-list">
             <div v-for="(activity, index) in recentActivities" :key="activity.id" class="activity-item">
               <div class="activity-icon" :class="index % 2 === 0 ? 'gold-bg' : 'blue-bg'">
                 <v-icon size="20" color="white">{{ activity.icon }}</v-icon>
               </div>
               <div class="activity-content">
                 <p class="activity-text">{{ activity.text }}</p>
                 <span class="activity-time">{{ activity.time }}</span>
               </div>
             </div>
           </div>
         </div>

         <!-- Quick Actions Card -->
         <div class="content-card actions-card">
           <div class="card-header">
             <h3 class="card-title">Quick Actions</h3>
           </div>
           <div class="actions-list">
             <!-- Add User - Only for Executive and Admin -->
             <div 
               v-if="showUserManagement"
               class="action-item" 
               @click="navigateToAddUser"
             >
               <div class="action-icon user-action gold-bg">
                 <v-icon size="24" color="white">mdi-account-plus</v-icon>
               </div>
               <div class="action-content">
                 <h4 class="action-title">Add User</h4>
                 <p class="action-desc">Create a new user account</p>
               </div>
                  <v-icon color="#002060">mdi-chevron-right</v-icon>
             </div>

             <!-- Add Client - Available for Executive, Admin, and Employee -->
             <div 
               v-if="showClientManagement"
               class="action-item" 
               @click="navigateToAddClient"
             >
               <div class="action-icon client-action blue-bg">
                 <v-icon size="24" color="white">mdi-account-tie</v-icon>
               </div>
               <div class="action-content">
                 <h4 class="action-title">Add Client</h4>
                 <p class="action-desc">Register a new client</p>
               </div>
                <v-icon color="#002060">mdi-chevron-right</v-icon>
             </div>

             <!-- Invite Member - Only for Executive and Admin -->
             <div 
               v-if="showUserManagement"
               class="action-item" 
               @click="navigateToInviteMember"
             >
               <div class="action-icon invite-action gold-bg">
                 <v-icon size="24" color="white">mdi-email-plus</v-icon>
               </div>
               <div class="action-content">
                 <h4 class="action-title">Invite Member</h4>
                 <p class="action-desc">Send team invitation</p>
               </div>
                <v-icon color="#002060">mdi-chevron-right</v-icon>
             </div>

             <!-- View Q&A - Available for all roles except QA Agent -->
             <div 
               class="action-item" 
               @click="navigateToQA"
             >
               <div class="action-icon qa-action blue-bg">
                 <v-icon size="24" color="white">mdi-help-circle</v-icon>
               </div>
               <div class="action-content">
                 <h4 class="action-title">View Q&A</h4>
                 <p class="action-desc">Manage client questions</p>
               </div>
                <v-icon color="#002060">mdi-chevron-right</v-icon>
             </div>


           </div>
         </div>
       </div>

             <!-- Charts and Analytics Row - Only for Executive and Admin -->
       <div v-if="showAnalytics && showDashboardForQAAgent" class="analytics-row">
        <!-- User Growth Chart -->
        <div class="content-card chart-card">
          <div class="card-header">
            <h3 class="card-title">User Growth</h3>
            <v-btn variant="text" size="small" color="#002060">Last 6 Months</v-btn>
          </div>
                     <div class="chart-container">
             <apexchart
               :key="'user-growth-' + stats.totalUsers"
               type="area"
               height="280"
               :options="userGrowthOptions"
               :series="userGrowthSeries"
             />
           </div>
        </div>

        <!-- Client Distribution Chart -->
        <div class="content-card chart-card">
          <div class="card-header">
            <h3 class="card-title">Client Distribution</h3>
            <v-btn variant="text" size="small" color="#002060">By Membership</v-btn>
          </div>
                     <div class="chart-container">
             <apexchart
               :key="'client-distribution-' + stats.totalClients"
               type="donut"
               height="280"
               :options="clientDistributionOptions"
               :series="clientDistributionSeries"
             />
           </div>
        </div>
      </div>

             <!-- Bottom Row - Recent Users - Only for Executive and Admin -->
       <div v-if="showRecentUsers && showDashboardForQAAgent" class="recent-section">
        <div class="content-card recent-card">
          <div class="card-header">
            <h3 class="card-title">Recent Users</h3>
            <v-btn variant="text" size="small" color="#002060" @click="$router.push({ name: 'Members' })">
              View All Users
            </v-btn>
          </div>
          <div class="recent-users">
            <div v-for="user in recentUsers" :key="user.id" class="user-item">
              <v-avatar size="40" color="#002060">
                <span class="avatar-text">{{ user.initials }}</span>
              </v-avatar>
              <div class="user-info">
                <p class="user-name">{{ user.name }}</p>
                <span class="user-role">{{ user.role }}</span>
              </div>
              <span class="user-date">{{ user.dateAdded }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Notification Dialog -->
      <NotificationDialog
        v-model:visible="showNotification"
        :type="notificationType"
        :title="notificationTitle"
        :message="notificationMessage"
        @close="showNotification = false"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import NotificationDialog from '../components/NotificationDialog.vue';
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'Dashboard',
  components: {
    NotificationDialog,
    apexchart: VueApexCharts
  },
  computed: {
    // Role-based computed properties
    isExecutive() {
      return this.$store.getters.userRole === 'Executive';
    },
    isAdmin() {
      return this.$store.getters.userRole === 'Admin';
    },
    isEmployee() {
      return this.$store.getters.userRole === 'Employee';
    },
    isQAAgent() {
      return this.$store.getters.userRole === 'QA Agent';
    },
    
    // Show different content based on role
    showUserManagement() {
      return this.isExecutive || this.isAdmin;
    },
    showClientManagement() {
      return this.isExecutive || this.isAdmin || this.isEmployee;
    },
    showQAManagement() {
      return true; // All roles can access QA
    },
    showAnalytics() {
      return this.isExecutive || this.isAdmin;
    },
    showRecentUsers() {
      return this.isExecutive || this.isAdmin;
    },
    showActivityLog() {
      return this.isExecutive || this.isAdmin;
    },
    // QA Agent specific restrictions
    showDashboardForQAAgent() {
      return !this.isQAAgent; // QA Agents should not see dashboard
    },
    showStatsForQAAgent() {
      return !this.isQAAgent; // QA Agents should not see stats
    },
    showQuickActionsForQAAgent() {
      return !this.isQAAgent; // QA Agents should not see quick actions
    },
    
    userDisplayName() {
      const user = this.$store.getters.user;
      if (user && user.displayName) {
        return user.displayName;
      }
      return user?.email?.split('@')[0] || 'User';
    }
  },
  data() {
    return {
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      stats: {
        totalUsers: 0,
        totalClients: 0,
        totalMembers: 0,
        pendingQuestions: 0,
        answeredToday: 8,
        teamMembers: 0,
        monthlyGrowth: 15.2,
        domesticMembers: 0,
        individualMembers: 0,
        individualAndDomesticMembers: 0
      },
      qaWorkload: {
        pending: 0,
        total: 0
      },
      requestingQuestions: false,
      recentActivities: [],
      recentUsers: [],
      // User Growth Chart Configuration
      userGrowthSeries: [{
        name: 'Users',
        data: [30, 40, 45, 50, 49, 60, 70, 91, 125, 156]
      }],
      userGrowthOptions: {
        chart: {
          type: 'area',
          height: 280,
          toolbar: {
            show: false
          },
          background: 'transparent'
        },
        colors: ['#002060'],
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.5,
            gradientToColors: ['#BCA258'],
            inverseColors: false,
            opacityFrom: 0.8,
            opacityTo: 0.1,
            stops: [0, 100]
          }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        grid: {
          show: true,
          borderColor: '#f1f1f1'
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct']
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: function (val) {
              return val + " users"
            }
          }
        }
      },
      // Client Distribution Chart Configuration
      clientDistributionSeries: [0, 0, 0, 0],
      clientDistributionOptions: {
        chart: {
          type: 'donut',
          height: 280
        },
        colors: ['#BCA258', '#8e8da4', '#002060', '#9c27b0'],
        labels: ['Domestic', 'Individual', 'Individual & Domestic'],
        legend: {
          show: true,
          position: 'bottom'
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#002060'
                },
                value: {
                  show: true,
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#002060'
                },
                total: {
                  show: true,
                  label: 'Total',
                  color: '#8e8da4'
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: false
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: function (val) {
              return val + " clients"
            }
          }
        }
      }
    };
  },
  created() {
    this.loadDashboardData();
    this.logDashboardAccess();
  },
  mounted() {
    // Ensure charts are properly initialized after component is mounted
    this.$nextTick(() => {
      // Force chart re-render
      this.$forceUpdate();
    });
  },
  beforeUnmount() {
    // Clean up listeners
    if (this.unsubscribeUsers) this.unsubscribeUsers();
    if (this.unsubscribeClients) this.unsubscribeClients();
    if (this.unsubscribeMembers) this.unsubscribeMembers();
    if (this.unsubscribeActivities) this.unsubscribeActivities();
    if (this.unsubscribeQAWorkload) this.unsubscribeQAWorkload();
  },
  methods: {
    loadDashboardData() {
      console.log('Loading live dashboard data...');
      try {
        this.loadUsers();
        this.loadClients();
        this.loadMembers();
        this.loadRecentActivities();
        this.loadQAWorkload();
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      }
    },
    
    loadUsers() {
      // Listen for real-time updates from users collection
      const usersQuery = query(collection(db, 'users'));
      this.unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
        const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        this.stats.totalUsers = users.length;
        this.stats.teamMembers = users.filter(user => ['Admin', 'manager'].includes(user.role)).length;
        
        // Get recent users (last 3)
        this.recentUsers = users
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 3)
          .map(user => ({
            id: user.id,
            name: `${user.firstName} ${user.surname}`,
            role: user.role,
            initials: `${user.firstName?.[0] || ''}${user.surname?.[0] || ''}`,
            dateAdded: user.dateAdded
          }));
      }, (error) => {
        console.error('Error loading users:', error);
        this.stats.totalUsers = 0;
        this.stats.teamMembers = 0;
      });
    },
    
    loadClients() {
      // Listen for real-time updates from clients collection
      const clientsQuery = query(collection(db, 'clients'));
      this.unsubscribeClients = onSnapshot(clientsQuery, (snapshot) => {
        const clients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        this.stats.totalClients = clients.length;
        
        // Count by membership level
        this.stats.domesticMembers = clients.filter(c => c.membership === 'Domestic').length;
        this.stats.individualMembers = clients.filter(c => c.membership === 'Individual').length;
        this.stats.individualAndDomesticMembers = clients.filter(c => c.membership === 'Individual & Domestic').length;
        
        // Update chart data
        this.clientDistributionSeries = [
          this.stats.domesticMembers,
          this.stats.individualMembers,
          this.stats.individualAndDomesticMembers
        ];
        
        console.log('Chart data updated:', this.clientDistributionSeries);
      }, (error) => {
        console.error('Error loading clients:', error);
        this.stats.totalClients = 0;
      });
    },
    
    loadMembers() {
      // Listen for real-time updates from members collection
      const membersQuery = query(collection(db, 'members'));
      this.unsubscribeMembers = onSnapshot(membersQuery, (snapshot) => {
        const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        this.stats.totalMembers = members.length;
      }, (error) => {
        console.error('Error loading members:', error);
        this.stats.totalMembers = 0;
      });
    },

    loadQAWorkload() {
      if (!this.isQAAgent) return;
      
      const currentUserName = this.userDisplayName;
      const questionsQuery = query(collection(db, 'questions'), where('assignedTo', '==', currentUserName));
      
      this.unsubscribeQAWorkload = onSnapshot(questionsQuery, (snapshot) => {
        const questions = snapshot.docs.map(doc => doc.data());
        this.qaWorkload.total = questions.length;
        this.qaWorkload.pending = questions.filter(q => q.status === 'pending' || q.status === 'in_progress').length;
      }, (error) => {
        console.error('Error loading QA workload:', error);
        this.qaWorkload.total = 0;
        this.qaWorkload.pending = 0;
      });
    },

    loadRecentActivities() {
      // Listen for real-time updates from activities collection - get latest 4
      const activitiesQuery = query(
        collection(db, 'activities'), 
        orderBy('timestamp', 'desc'), 
        limit(4)
      );
      
      this.unsubscribeActivities = onSnapshot(activitiesQuery, (snapshot) => {
        this.recentActivities = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            type: this.getActivityType(data.action),
            icon: this.getActivityIcon(data.action),
            text: data.target ? `${data.details} - ${data.target}` : data.details,
            time: this.getTimeAgo(data.timestamp?.toDate() || new Date(data.timestamp))
          };
        });
      }, (error) => {
        console.error('Error loading activities:', error);
        this.recentActivities = [];
      });
    },

    getActivityType(action) {
      const typeMap = {
        'Login': 'user',
        'Logout': 'user',
        'User Created': 'user',
        'User Updated': 'user',
        'User Deleted': 'user',
        'Client Created': 'client',
        'Client Updated': 'client',
        'Client Deleted': 'client',
        'Question Created': 'question',
        'Question Updated': 'question',
        'Question Deleted': 'question',
        'Profile Updated': 'user',
        'Password Changed': 'user',
        'Member Invited': 'member'
      };
      return typeMap[action] || 'user';
    },

    getActivityIcon(action) {
      const iconMap = {
        'Login': 'mdi-login',
        'Logout': 'mdi-logout',
        'User Created': 'mdi-account-plus',
        'User Updated': 'mdi-account-edit',
        'User Deleted': 'mdi-account-remove',
        'Client Created': 'mdi-account-group',
        'Client Updated': 'mdi-account-edit',
        'Client Deleted': 'mdi-account-remove',
        'Question Created': 'mdi-help-circle',
        'Question Updated': 'mdi-help-circle-outline',
        'Question Deleted': 'mdi-delete',
        'Profile Updated': 'mdi-account-cog',
        'Password Changed': 'mdi-lock-reset',
        'Member Invited': 'mdi-email-send'
      };
      return iconMap[action] || 'mdi-information';
    },

    getTimeAgo(date) {
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
      if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
      return date.toLocaleDateString();
    },

    navigateToAddUser() {
      this.$router.push({ name: 'AddUser' });
    },

    navigateToAddClient() {
      this.$router.push({ name: 'AddClient' });
    },

    navigateToInviteMember() {
      this.$router.push({ name: 'InviteMember' });
    },

    navigateToQA() {
      this.$router.push({ name: 'Qa' });
    },

    navigateToCompletedQuestions() {
      this.$router.push({ name: 'CompletedQuestions' });
    },

    logDashboardAccess() {
      const currentUser = this.$store.getters.user;
      const currentUserId = currentUser?.uid;
      const currentUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
      
      ActivityLogger.dashboardAccessed(currentUserName, currentUserId, 'Dashboard Page');
    },
    
    getWelcomeMessage() {
      const userRole = this.$store.getters.userRole;
      
      switch (userRole) {
        case 'Executive':
          return 'You have full system access. Monitor all activities, manage users, and view comprehensive analytics.';
        case 'Admin':
          return 'You have administrative access. Manage users, clients, and view system analytics.';
        case 'Employee':
          return 'You can manage client profiles and access QA records. Focus on client service and support.';
        case 'QA Agent':
          return 'You can view and answer questions specifically assigned to you. Focus on providing quality support to clients.';
        default:
          return 'Welcome to the DMM LAW portal.';
      }
    },

    async requestMoreQuestions() {
      if (!this.isQAAgent) return;
      
      this.requestingQuestions = true;
      
      try {
        const currentUserName = this.userDisplayName;
        
        // Import the QaAssignmentManager
        const { QaAssignmentManager } = await import('../utils/qaAssignment');
        
        // Auto-assign up to 3 more questions
        const assignedQuestionIds = await QaAssignmentManager.autoAssignQuestions(currentUserName, 3);
        
        if (assignedQuestionIds.length > 0) {
          this.notificationType = 'success';
          this.notificationTitle = 'Questions Assigned!';
          this.notificationMessage = `${assignedQuestionIds.length} new questions have been assigned to you.`;
        } else {
          this.notificationType = 'info';
          this.notificationTitle = 'No Questions Available';
          this.notificationMessage = 'There are currently no unassigned questions available.';
        }
        
        this.showNotification = true;
        
      } catch (error) {
        console.error('Error requesting more questions:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error';
        this.notificationMessage = 'Failed to assign more questions. Please try again.';
        this.showNotification = true;
      } finally {
        this.requestingQuestions = false;
      }
    },

    navigateToQA() {
      this.$router.push({ name: 'Qa' });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.dashboard-bg {
  min-height: 100vh;
  background: #f2f2f2;
  padding: 1rem;
}

.dashboard-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.welcome-section {
  margin-bottom: 30px;
  text-align: center;
}

.welcome-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #002060;
  margin-bottom: 10px;
}

.welcome-subtitle {
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
}

.qa-agent-section {
  margin-bottom: 30px;
}

.qa-agent-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  text-align: center;
  padding: 20px 0;
}

.qa-agent-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 500px;
}

.qa-agent-icon {
  margin-bottom: 8px;
}

.qa-agent-text h4 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #002060;
  margin: 0 0 12px 0;
}

.qa-agent-text p {
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

.qa-agent-actions {
  display: flex;
  justify-content: center;
}

.qa-action-btn {
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.users-icon { background: linear-gradient(135deg, #BCA258 0%, #a38b4c 100%); }
.clients-icon { background: linear-gradient(135deg, #002060 0%, #001547 100%); }
.questions-icon { background: linear-gradient(135deg, #BCA258 0%, #a38b4c 100%); }
.members-icon { background: linear-gradient(135deg, #002060 0%, #001547 100%); }

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #002060;
  margin: 0 0 0.25rem 0;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  margin: 0 0 0.5rem 0;
}

.stat-change {
  font-size: 0.8rem;
  font-weight: 500;
}

.stat-change.positive { color: #4caf50; }
.stat-change.neutral { color: #666; }

.content-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
}

.analytics-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.recent-section {
  display: grid;
  grid-template-columns: 1fr;
}

.content-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.card-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #002060;
  margin: 0;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.activity-item:hover {
  background: #fff;
  border-color: #002060;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 32, 96, 0.1);
}

.activity-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gold-bg {
  background: linear-gradient(135deg, #BCA258 0%, #a38b4c 100%) !important;
}

.blue-bg {
  background: linear-gradient(135deg, #002060 0%, #001547 100%) !important;
}

.green-bg {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
}

.activity-content {
  flex: 1;
}

.activity-text {
  font-size: 1rem;
  font-weight: 500;
  color: #002060;
  margin: 0 0 6px 0;
}

.activity-time {
  font-size: 0.85rem;
  color: #666;
  font-weight: 400;
}

.actions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-item:hover {
  background: #002060;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 32, 96, 0.2);
}

.action-item:hover .action-title,
.action-item:hover .action-desc {
  color: white;
}

.action-item:hover .action-icon {
  background: rgba(255, 255, 255, 0.2) !important;
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

/* Action icon role markers retained for potential future styling */
/* .user-action, .client-action, .invite-action, .qa-action, .completed-qa-action {} */

.action-content {
  flex: 1;
}

.action-title {
  font-size: 1rem;
  font-weight: 600;
  color: #002060;
  margin: 0 0 4px 0;
  transition: color 0.2s ease;
}

.action-desc {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
  transition: color 0.2s ease;
}

.chart-container {
  height: 300px;
  width: 100%;
  position: relative;
}

.chart-container .apexcharts-canvas {
  width: 100% !important;
  height: 100% !important;
}

.recent-users {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.user-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.user-item:hover {
  background: #f8f9fa;
}

.avatar-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
}

.user-info {
  flex: 1;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px 0;
}

.user-role {
  font-size: 0.8rem;
  color: #666;
}

.user-date {
  font-size: 0.8rem;
  color: #999;
}

@media (max-width: 1200px) {
  .content-row {
    grid-template-columns: 1fr;
  }
  
  .analytics-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-bg {
    padding: 16px;
  }
  
  .stats-row {
    grid-template-columns: 1fr;
  }
  
  .action-item {
    padding: 12px;
  }
  
  .action-icon {
    width: 40px;
    height: 40px;
  }
}
</style>
  